import { cacheUtils } from "../..";
import { state, ui } from "../../interfaces/interfaces";
import { dispatchSetMultipleState } from "@reducers/stateReducer";
import {
  dispatchSetSnackMsgError,
  dispatchSetSnackMsgInfo,
  dispatchSetSnackMsgSuccess,
} from "@reducers/uiReducer";
import { logger } from "@services/workers/helpers";
import { TemplateSTATEObject } from "@services/storage/storeTemplate";
import { CacheTypes } from "../cache/interfaces";
import { roundToTwoDecimalPlaces } from "../general";
import {
  RightioWBSAPIModelsDiscounts,
  RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult,
} from "../apis/wbs/model";
import { CARE_PLAN_COMBO_DISCOUNT_ID, CARE_PLAN_DISCOUNT_ID } from "@src/constants";

const nameSpace = "charges";

export const applyChargeRateDiscount = () => {
  logger.debug(nameSpace + "->" + applyChargeRateDiscount.name, "Applying discount");
  const chargeObject = {
    isPrice: state.IsFixedPrice ? true : false,
    rate: 0,
    originalRate: state.ChargeRateOriginalRate ?? 0,
    price: 0,
    originalPrice: state.Charge?.price ?? 0,
    promoCode: ui.PromoCode ?? "",
    promoCodeIsPercentage: state.Discount?.isPercentage ? true : false,
    promoCodeDiscount: state.Discount?.discount ?? 0,
    promoCodeId: state.Discount?.discountId ?? 0,
    rateDiscountId: state.ChargeRateReductionDiscountId ?? 0,
    rateReduction: state.ChargeReductionDiscount ?? 0,
    rateReductionMin: state.ChargeReductionOriginalMaximumReducedRate ?? 0,
    rateCurrentlyAppliedReduction: ui.RatesDiscount ?? 0,
    rateReductionIsPercentage: state.ChargeReductionIsPercentage ? true : false,
    rateReductionDetails: state.ChargeReductionDiscountDetails ?? "",
    promoDiscountDetails: state.Discount?.discountDescription ?? "",

    cap: state.PriceCap ?? 0,
    ccDiscountTypeIsPercentage: true,
    _isPromoDiscountApplied: false,
    _isCapped: false,
    get areBothDiscountsApplicable() {
      return this.isPromoDiscountApplicable && this.isRateReduced && this.rateDiscountId === CARE_PLAN_DISCOUNT_ID;
    },
    get isPromoDiscountApplied() {
      return this._isPromoDiscountApplied;
    },
    set isPromoDiscountApplied(value) {
      this._isPromoDiscountApplied = value;
    },
    get isPromoDiscountApplicable() {
      return this.promoCodeDiscount > 0;
    },
    get isRateReduced() {
      return this.rateReduction > 0;
    },
    get isRateReductionApplied() {
      return this.rateCurrentlyAppliedReduction > 0;
    },
    get isCapped() {
      return this._isCapped;
    },
    set isCapped(value) {
      this._isCapped = value;
    },
    applyPromoCodeAsPercentageToPrice() {
      logger.debug(
        nameSpace + "->" + this.applyPromoCodeAsPercentageToPrice.name,
        "Applying promo code as percentage to price"
      );
      // percentage
      this.price = this.originalPrice - (this.originalPrice * this.promoCodeDiscount) / 100;
    },
    applyPromoCodeAndRateReductionAsPercentageToPrice() {
      logger.debug(
        nameSpace + "->" + this.applyPromoCodeAndRateReductionAsPercentageToPrice.name,
        "Applying promo code and rate reduction as percentage to price"
      );
      // percentage
      this.price = this.originalPrice - (this.originalPrice * (this.promoCodeDiscount + this.rateReduction)) / 100;
    },
    applyPromoCodeAsFixedAndRateAsPercentageToPrice() {
      logger.debug(
        nameSpace + "->" + this.applyPromoCodeAsFixedAndRateAsPercentageToPrice.name,
        "Applying promo code as fixed and rate as percentage to price"
      );
      // fixed
      const tempPrice = this.originalPrice - this.promoCodeDiscount;
      this.price = tempPrice - (tempPrice * this.rateReduction) / 100;
    },
    applyRateReductionAsPercentageToPrice() {
      logger.debug(
        nameSpace + "->" + this.applyRateReductionAsPercentageToPrice.name,
        "Applying rate reduction as percentage to price"
      );
      // percentage
      this.price = this.originalPrice - (this.originalPrice * this.rateReduction) / 100;
    },
    applyRateReductionAsFixedToPrice() {
      logger.debug(
        nameSpace + "->" + this.applyRateReductionAsFixedToPrice.name,
        "Applying rate reduction as fixed to price"
      );
      // fixed
      this.price = this.originalPrice - this.rateReduction;
    },
    applyPromoCodeToPrice() {
      // check if promo code is percentage or fixed amount
      if (this.promoCodeIsPercentage) {
        this.applyPromoCodeAsPercentageToPrice();
        return;
      } // if fixed amount
      this.price = this.originalPrice - this.promoCodeDiscount;
    },
    applyBothDiscountsToPrice() {
      // check if promo code is percentage or fixed amount
      if (this.promoCodeIsPercentage) {
        // percentage
        this.applyPromoCodeAndRateReductionAsPercentageToPrice();
        return;
      } // if fixed amount
      this.applyPromoCodeAsFixedAndRateAsPercentageToPrice();
    },
    applyPriceDiscount() {
      if (this.areBothDiscountsApplicable) {
        this.applyBothDiscountsToPrice();
        this.isPromoDiscountApplied = true;
        return;
      } else if (this.isPromoDiscountApplicable) {
        this.applyPromoCodeToPrice();
        this.isPromoDiscountApplied = true;
        return;
      } else if (this.isRateReduced && this.rateDiscountId === CARE_PLAN_DISCOUNT_ID) {
        this.applyRateReductionAsPercentageToPrice();
        return;
      } else if (this.isRateReduced && this.rateDiscountId === CARE_PLAN_COMBO_DISCOUNT_ID) {
        this.applyRateReductionAsFixedToPrice();
        return;
      }
      this.price = this.originalPrice;
    },
    applyPromoCodeAndRateReductionAsPercentageToRate() {
      logger.debug(
        nameSpace + "->" + this.applyPromoCodeAndRateReductionAsPercentageToRate.name,
        "Applying promo code and rate reduction as percentage to rate"
      );
      // percentage
      const reducedRate = this.originalRate - (this.originalRate * (this.promoCodeDiscount + this.rateReduction)) / 100;
      this.isCapped = reducedRate < this.cap && this.rateDiscountId !== CARE_PLAN_DISCOUNT_ID;
      this.rate = this.isCapped ? Math.max(this.cap, reducedRate) : reducedRate;
    },
    applyPromoCodeAsFixedAndRateAsPercentageToRate() {
      logger.debug(
        nameSpace + "->" + this.applyPromoCodeAsFixedAndRateAsPercentageToRate.name,
        "Applying promo code as fixed and rate as percentage to rate"
      );
      // fixed
      const tempRate = this.originalRate - this.promoCodeDiscount;
      const reducedRate = tempRate - (tempRate * this.rateReduction) / 100;
      this.isCapped = reducedRate < this.cap && this.rateDiscountId !== CARE_PLAN_DISCOUNT_ID;
      this.rate = this.isCapped ? Math.max(this.cap, reducedRate) : reducedRate;
    },
    applyPromoCodeAsFixedToRate() {
      logger.debug(nameSpace + "->" + this.applyPromoCodeAsFixedToRate.name, "Applying promo code as fixed to rate");
      // fixed
      const reducedRate = this.originalRate - this.promoCodeDiscount;
      this.isCapped = reducedRate < this.cap;
      this.rate = Math.max(this.cap, reducedRate);
    },
    applyPromoCodeAsPercentageToRate() {
      logger.debug(
        nameSpace + "->" + this.applyPromoCodeAsPercentageToRate.name,
        "Applying promo code as percentage to rate"
      );
      // percentage
      this.rate = this.originalRate - (this.originalRate * this.promoCodeDiscount) / 100;
    },
    applyPromoDiscountToRate() {
      // check if promo code is percentage or fixed amount
      if (this.promoCodeIsPercentage) {
        // percentage
        this.applyPromoCodeAsPercentageToRate();
        return;
      } // if fixed amount
      this.applyPromoCodeAsFixedToRate();
    },
    applyBothDiscountsToRate() {
      // check if promo code is percentage or fixed amount
      if (this.promoCodeIsPercentage) {
        // percentage
        this.applyPromoCodeAndRateReductionAsPercentageToRate();
        return;
      } // if fixed amount
      this.applyPromoCodeAsFixedAndRateAsPercentageToRate();
    },
    applyRateReductionsAsPercentageToRate() {
      logger.debug(
        nameSpace + "->" + this.applyRateReductionsAsPercentageToRate.name,
        "Applying rate reductions as percentage to rate"
      );
      // percentage
      const reducedRate = this.originalRate - (this.originalRate * this.rateReduction) / 100;
      if (this.isRateReductionApplied) {
        this.rate = Math.max(Math.min(this.rateCurrentlyAppliedReduction, reducedRate), this.rateReductionMin);
      }
      this.rate = reducedRate;
    },
    applyRateReductionsAsFixedToRate() {
      logger.debug(
        nameSpace + "->" + this.applyRateReductionsAsFixedToRate.name,
        "Applying rate reductions as fixed to rate"
      );
      // fixed
      const reducedRate = this.originalRate - this.rateReduction;
      if (this.isRateReductionApplied) {
        this.rate = Math.max(Math.min(this.rateCurrentlyAppliedReduction, reducedRate), this.rateReductionMin);
        return;
      }
      this.rate = reducedRate;
    },
    applyRateReduction() {
      if (this.isRateReduced) {
        // check if promo code is percentage or fixed amount
        if (this.rateReductionIsPercentage) {
          // percentage
          this.applyRateReductionsAsPercentageToRate();
          return;
        } // if fixed amount
        this.applyRateReductionsAsFixedToRate();
        return;
      }
      // no reduction
      this.rate = this.originalRate;
    },
    applyRateDiscount() {
      if (this.areBothDiscountsApplicable) {
        this.applyBothDiscountsToRate();
        this.isPromoDiscountApplied = true;
        return;
      }
      if (this.isPromoDiscountApplicable) {
        this.applyPromoDiscountToRate();
        this.isPromoDiscountApplied = true;
        return;
      }
      this.applyRateReduction();
    },
    get reduction() {
      const reduction = this.isPrice ? this.price : this.rate;
      return roundToTwoDecimalPlaces(reduction);
    },
    get trace() {
      if (this.areBothDiscountsApplicable && this.isPromoDiscountApplied) {
        return `${state.Charge?.trace} - [Promo: ${this.promoDiscountDetails}] - [${this.rateReductionDetails}]`;
      }
      if (this.isPromoDiscountApplied) {
        return `${state.Charge?.trace} - [Promo: ${this.promoDiscountDetails}${this.isCapped ? " - capped at £" + this.cap : ""
          }]`;
      }
      if (this.isRateReduced) {
        console.log("this.rateReductionDetails", this.rateReductionDetails);
        return `${state.Charge?.trace} - [${this.rateReductionDetails}]`;
      }
      return state.Charge?.trace;
    },
  };

  // if price is fixed, then we need to calculate the price discount
  if (chargeObject.isPrice) {
    chargeObject.applyPriceDiscount();
  }
  // if price is not fixed, then we need to calculate the rate discount
  else {
    chargeObject.applyRateDiscount();
  }

  dispatchSetMultipleState({
    ChargeReductionAdjustmentRate: chargeObject.reduction,
    PromoCodeApplied: chargeObject.isPromoDiscountApplied,
    ChargeRateAdj: !chargeObject.isPrice ? chargeObject.reduction : chargeObject.originalRate,
    ChargePriceAdj: chargeObject.isPrice ? chargeObject.reduction : chargeObject.originalPrice,
    IsCapped: chargeObject.isCapped,
    ChargePromoDiscountDetails: chargeObject.promoDiscountDetails,
    ChargePromoDiscountId: chargeObject.promoCodeId,
    ChargeTrace: chargeObject.trace || undefined,
  });

  // [${state.ChargeReductionDiscountDetails}]`,
  // ChargePromoDiscountDetails

  ui.RatesDiscount = chargeObject.reduction;

  // state.ChargeRateAdj = calculateCostAfterVAT(state.ChargeReductionAdjustmentRate);
  logger.debug(nameSpace + "->" + applyChargeRateDiscount.name, "Charge reduction calculated");
};

export const removePromoCodeDiscount = () => {
  //reset the promo code discount
  state.Discount = undefined;
  // display message
  dispatchSetSnackMsgSuccess({
    ProblemMsg: "Discount removed",
  });

  logger.debug(nameSpace + "->" + removePromoCodeDiscount.name, "Discount removed");
  applyChargeRateDiscount();
};

export const applyPromoCodeDiscount = () => {
  const discountCode = ui.PromoCode;
  if (!discountCode) return;
  const discount = isPromoCodeDiscountValid(discountCode);
  if (discount) {
    state.Discount = discount as unknown as RightioWBSAPIModelsDiscounts;
  }
  applyChargeRateDiscount();
};

export const isPromoCodeDiscountValid = (discountCode: string) => {
  const discount = getPromoCodeDiscountFromCache(discountCode);
  if (discount) {
    const isValid = discount.isInUse && discount.inUseTo && new Date(discount.inUseTo).getTime() > new Date().getTime();
    // if is valid display message
    if (isValid) {
      logger.debug(nameSpace + "->" + isPromoCodeDiscountValid.name, "Discount applied");
      dispatchSetSnackMsgSuccess({
        ProblemMsg: `Discount ${discountCode} is valid`,
      });
    } else {
      dispatchSetSnackMsgError({
        ProblemMsg: `Discount ${discountCode} is not valid`,
      });
      logger.debug(nameSpace + "->" + isPromoCodeDiscountValid.name, "Discount not valid");
    }
    return discount;
  }
  // display message
  dispatchSetSnackMsgError({
    ProblemMsg: `Discount ${discountCode} not found`,
  });
  logger.debug(nameSpace + "->" + isPromoCodeDiscountValid.name, "Discount not found");
  return undefined;
};

export const getPromoCodeDiscountFromCache = (discountCode: string) =>
  cacheUtils.getCache<CacheTypes.Discounts>("Discounts").find((d) => d.code === discountCode.toUpperCase());

export const isPromoCodeValid = (discountCode: string) => {
  const discount = getPromoCodeDiscountFromCache(discountCode);
  if (discount) {
    const isValid = discount.isInUse && discount.inUseTo && new Date(discount.inUseTo).getTime() > new Date().getTime();
    // if is valid display message
    if (isValid) {
      state.PromoCodeValid = true;
      return;
    }
    state.PromoCodeValid = false;
    return;
  }
  state.PromoCodeValid = false;
  return;
};

export const setRate = (charge: RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult) => {
  logger.debug(nameSpace + "->" + setRate.name, "Charge", charge);
  const chargeRateObject: Partial<TemplateSTATEObject> = {};
  chargeRateObject.ChargeRate = charge.rate || 0;
  chargeRateObject.ChargeRateOriginalRate = charge.rate || 0;
  chargeRateObject.ChargePrice = charge.price || 0;
  chargeRateObject.ChargeRateOriginalPrice = charge.price || 0;
  chargeRateObject.ChargeRatePeriod = charge.period || 0;
  chargeRateObject.ChargeRateSubPeriod = charge.subPeriod || 0;
  chargeRateObject.ChargeRateSubRate1 = charge.subRate1 || 0;
  chargeRateObject.ChargeRateSubRate2 = charge.subRate2 || 0;
  chargeRateObject.ChargeRateSubRate3 = charge.subRate3 || 0;
  chargeRateObject.ChargeIsVATInclusive = charge.isVATInclusive || false;
  chargeRateObject.ChargeTrace = charge.trace || undefined;
  chargeRateObject.ChargeCardRequired = charge.isCardRequired;
  chargeRateObject.ChargeReductionMaximumReducedRate = charge.maximumReducedRate || 0;
  chargeRateObject.ChargeReductionOriginalMaximumReducedRate = charge.maximumReducedRate || 0;

  chargeRateObject.ChargeRateAdj = chargeRateObject.ChargeRate;
  chargeRateObject.ChargePriceAdj = chargeRateObject.ChargePrice;

  chargeRateObject.JobRate1 = chargeRateObject.ChargeRateAdj;
  chargeRateObject.JobPrice1 = chargeRateObject.ChargePriceAdj;
  switch (chargeRateObject.ChargeRatePeriod) {
    case 15: {
      chargeRateObject.ChargeRateTime = "qh";
      chargeRateObject.ChargeRateTimely = "quarter-hourly";
      break;
    }

    case 30: {
      chargeRateObject.ChargeRateTime = "hh";
      chargeRateObject.ChargeRateTimely = "half-hourly";
      break;
    }

    case 60: {
      chargeRateObject.ChargeRateTime = "h";
      chargeRateObject.ChargeRateTimely = "hourly";
      break;
    }

    default: {
      chargeRateObject.ChargeRateTime = chargeRateObject.ChargeRatePeriod + " mins";
      chargeRateObject.ChargeRateTimely = chargeRateObject.ChargeRatePeriod + " minutes";
      break;
    }
  }

  switch (chargeRateObject.ChargeRateSubPeriod) {
    case 15: {
      chargeRateObject.ChargeRateSubTime = "qh";
      chargeRateObject.ChargeRateSubTimely = "quarter-hourly";
      break;
    }

    case 30: {
      chargeRateObject.ChargeRateSubTime = "hh";
      chargeRateObject.ChargeRateSubTimely = "half-hourly";
      break;
    }

    case 60: {
      chargeRateObject.ChargeRateSubTime = "h";
      chargeRateObject.ChargeRateSubTimely = "hourly";
      break;
    }

    default: {
      chargeRateObject.ChargeRateSubTime = chargeRateObject.ChargeRatePeriod + " mins";
      chargeRateObject.ChargeRateSubTimely = chargeRateObject.ChargeRatePeriod + " minutes";
      break;
    }
  }
  // if (ui.RatesDiscount !== calculateCostAfterVAT(state.ChargeRateOriginalRate)) {
  if (ui.RatesDiscount !== chargeRateObject.ChargeRateOriginalRate) {
    // show warning about rates being changed
    dispatchSetSnackMsgInfo({
      ProblemMsg: "Rates updated due to change in circumstances",
    });
  }

  dispatchSetMultipleState(chargeRateObject);
  logger.debug(nameSpace + "->" + setRate.name, "ChargeRateObject", chargeRateObject);
};
